import { Button, Grid } from '@mui/material';
import React, { MouseEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { setOrderBy, setOrderDirection, setSelectedUserId, setSelectedUserIndex, setTriggerRefreshTable, setUsers, userManagementSelector } from './reducers/UserManagementReducer';
import _ from 'lodash';
import UserModel from '../Login/models/UserModel';
import UserApi from './api/UserApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { OrderDirection } from '../Datatable/OrderDirection';
import { setOpenModal, setSelectedAnalyticsUserId } from '../UserAnalytics/reducers/UserAnalyticsReducer';

const UserManagementTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();

    let userManagementData = useSelector(userManagementSelector);

    const columns = [
        {
            Field: "email",
            Title: "Email",
            AllowSort: true,
        } as DatatableColumnModel<UserModel>,
        {
            Field: "displayName",
            Title: "Display Name",
            AllowSort: true,
        } as DatatableColumnModel<UserModel>,
        {
            Field: "workingEmail",
            Title: "Working Email",
            AllowSort: true,
        } as DatatableColumnModel<UserModel>,
        {
            Field: "emailDisplayName",
            Title: "Email Display Name",
            AllowSort: true,
        } as DatatableColumnModel<UserModel>,
        {
            Field: "userGroup",
            Title: "User Group",
            AllowSort: true,
        } as DatatableColumnModel<UserModel>,
        {
            Field: "isLocked",
            Title: "Locked",
            AllowSort: true,
            Render: (rowData: UserModel) => (
                <>
                    {rowData.isLocked ? "Yes" : "No"}
                </>
            )
        } as DatatableColumnModel<UserModel>,
        {
            Field: "analytics",
            Title: "Stats",
            AllowSort: false,
            Render: (rowData: UserModel) => (
                <>
                    <div>Active since: {rowData.analytics.lastActiveDate}</div>
                    <div>Input: {rowData.analytics.inputToday}/{rowData.analytics.inputMonth}/{rowData.analytics.inputYear} this w/m/y</div>
                    <div>Search: {rowData.analytics.searchToday}/{rowData.analytics.searchMonth}/{rowData.analytics.searchYear} this w/m/y</div>
                </>
            )
        } as DatatableColumnModel<UserModel>,
        {
            Field: "analytics",
            Title: "Stats Details",
            AllowSort: false,
            Render: (rowData: UserModel) => (
                <>
                    <Button
                        onClick={() => {
                            dispatch(setSelectedAnalyticsUserId(rowData.id));
                            dispatch(setOpenModal(true));
                        }}
                    >
                        <i className="fa fa-bar-chart" aria-hidden="true"></i>
                    </Button>
                </>
            )
        } as DatatableColumnModel<UserModel>,
    ];

    const getTableClassName = () => {
        let className = "user-management-datatable-search-result nice-scroll";

        if (!userManagementData.inputExpand) {
            className += " user-management-datatable-search-result-input-collapse";
        }

        return className;
    }

    const getAll = () => {
        dispatch(showLoading(true));

        UserApi.GetAll()
            .then((response: BaseResponse<UserModel[]>) => {
                if (response.success) {
                    dispatch(setUsers(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }

    useEffect(() => {
        getAll()
    }, [userManagementData.triggerRefreshTable]);

    return <div className='datatable-wrapper'>
        <Datatable<UserModel>
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={userManagementData.users}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onRowSelectedChange={(user: UserModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedUserIndex(index));
                    dispatch(setSelectedUserId(user.id));
                }
            }}
            orderColumn={userManagementData.orderBy}
            orderDirection={userManagementData.orderDirection}
            selectedRowIndex={userManagementData.selectedIndex}
            stickyHeader={true}
            onOrderDirectionChange={(column: DatatableColumnModel<UserModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));

                let data = [...userManagementData.users];
                if (orderDirection == OrderDirection.Asc) {
                    data = _.sortBy(data, column.Field);
                } else {
                    data = _.sortBy(data, column.Field).reverse();
                }

                dispatch(setUsers(data));
            }}
        ></Datatable>
    </div>
};

export default UserManagementTable;